import { useState, useEffect } from 'react';
// eslint-disable-next-line import/no-named-default -- do want to change this file to much
import { default as ReactDatePicker } from 'react-datepicker';

import type { CSSProperties, ReactElement } from 'react';

import { ReactComponent as LockIcon } from 'Components/UI_Library/Icons/Lock.svg';
import { ReactComponent as Calendar } from 'Components/UI_Library/Icons/Calendar.svg';
import ToggleIndicator from 'Components/UI_Library/Icons/ToggleIndicator';
import Loader from 'Components/UI_Library/LoadingIndicators/Loader';
import classes from './DatePicker.module.css';

type Props = {
    /** The current value of the date picker */
    selected: Date | null;
    /** The function to update the value of the date picker */
    onSelect?: (_date: Date) => void;
    onChange?: (_date: Date) => void;
    /** The function to get called when leaving the date picker */
    onBlur?(event: React.FocusEvent<HTMLInputElement>): void;
    /** The optional <label> you can give the date picker. Take a string or JSX */
    label?: string | ReactElement;
    /** Renders <i>*</> next to the label if true */
    required?: boolean;
    /** Option to show the calendar svg icon on the left of the date picker */
    showCalendarIcon?: boolean;
    /** Option to show the toggle svg icon on the right of the date picker */
    showToggleIcon?: boolean;
    /** What the minimum date should be */
    minDate?: Date;
    /** What the maximum date should be */
    maxDate?: Date;
    /** The current value of the date picker, as ISO 8601 format */
    dateFormat?: string;
    /** Optional class(es) you can pass in */
    className?: string;
    /** Disable the date picker */
    disabled?: boolean;
    /** Loading state of the date picker */
    loading?: boolean;
    showTimeInput?: boolean | undefined;
    onCalendarClose?(): void;
    noBorder?: boolean;
    toggleStyles?: CSSProperties;
    /** Using react portal to overlay the popper */
    portalId?: string | ReactElement;
    /** Make the calendar height as same irrespective of days in months */
    fixedHeight?: boolean;
    /** Add style to the date picker popper */
    popperClassName?: string;
    /** Show the lock icon and don't show the date picker */
    showLocked?: boolean;
    /** Placement of date popup to the input field */
    popperPlacement?: string;
};

const DatePicker = (props: Props) => {
    const [toggled, setToggled] = useState(false);
    const [width, setWidth] = useState(window.innerWidth);

    const handleWindowSizeChange = () => {
        setWidth(window.innerWidth);
    };

    useEffect(() => {
        window.addEventListener('resize', handleWindowSizeChange);
        return () => {
            window.removeEventListener('resize', handleWindowSizeChange);
        };
    }, []);

    const { onSelect: _onSelect, onChange: _onChange, onBlur: _onBlur, onCalendarClose: _onCalendarClose } = props;
    return (
        <>
            {props.showLocked && (
                <div className={classes.showLocked}>
                    <span>
                        {props?.selected?.toLocaleDateString('en-UK', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                        })}
                    </span>
                    <div className={classes.lock}>
                        <LockIcon />
                    </div>
                </div>
            )}
            {!props.showLocked && (
                <div>
                    {props.label &&
                        (typeof props.label === 'string' ? (
                            <label className={classes.label}>
                                {props.label} {props.required && <i className={classes.required}>*</i>}
                            </label>
                        ) : (
                            props.label
                        ))}
                    <div className={classes.datePickerWrapper}>
                        {props.showCalendarIcon && (
                            <div className={classes.calendar}>
                                <Calendar />
                            </div>
                        )}

                        {props.showToggleIcon && !props.loading && (
                            <div className={classes.toggle} style={{ ...props.toggleStyles }}>
                                <ToggleIndicator toggled={toggled} />
                            </div>
                        )}

                        <div className={classes.datePickerBox} style={props.noBorder ? { border: 'none' } : {}}>
                            {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment -- This component is used everywhere don't want to alter it to much just for typescript */}
                            {/* @ts-ignore */}
                            <ReactDatePicker
                                dateFormat={props.dateFormat}
                                // eslint-disable-next-line @typescript-eslint/ban-ts-comment -- This component is used everywhere don't want to alter it to much just for typescript */
                                // @ts-ignore */
                                className={`${classes.datePickerDropdown} ${props.className ? props.className : ''}`}
                                {...(props.minDate && { minDate: props.minDate })}
                                {...(props.maxDate && { maxDate: props.maxDate })}
                                {...(props.disabled && { disabled: props.disabled })}
                                onFocus={() => setToggled(true)}
                                onBlur={() => setToggled(false)}
                                withPortal={width <= 768}
                                {...props}
                                selected={props.selected}
                                onSelect={props.onSelect}
                                {...(props.portalId && { portalId: props.portalId })}
                                {...(props.fixedHeight && { fixedHeight: props.fixedHeight })}
                                {...(props.popperClassName && { popperClassName: props.popperClassName })}
                                {...(props.popperPlacement && { popperPlacement: props.popperPlacement })}
                            />
                        </div>

                        {props.loading && (
                            <div className={classes.loading}>
                                <Loader type="in-button blue" />
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

DatePicker.defaultProps = {
    showCalendarIcon: true,
    showToggleIcon: true,
    dateFormat: 'do MMMM, yyyy',
};

export default DatePicker;
